import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Oralphysical } from "./Oralphysical";
import { Breastphysical } from "./Breastphysical";
import { Cervicalphysical } from "./Cervicalphysical";
import { ncdAPI } from "../../../../services/ncd.api";
import Loader from "../../../../components/Loader";
import dayjs from 'dayjs';

const PhysicalExaminationForm = forwardRef(({ individualId, gender }, ref) => {
  const today = new Date().toISOString().split("T")[0];
  const formatTodayApiDate = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // Section edit states (defaults to true; becomes false if existing data is fetched)
  const [isOralEditing, setIsOralEditing] = useState(true);
  const [isBreastEditing, setIsBreastEditing] = useState(true);
  const [isCervicalEditing, setIsCervicalEditing] = useState(true);

  // States to track if data was previously saved
  const [hasOralData, setHasOralData] = useState(false);
  const [hasBreastData, setHasBreastData] = useState(false);
  const [hasCervicalData, setHasCervicalData] = useState(false);

  const [formData, setFormData] = useState({
    oral: {
      assesDate: formatTodayApiDate(),
      oralLctns: {
        leukoplakiaPatch: [],
        erythroplakiaPatch: [],
        ulcerPatch: [],
        growthPatch: [],
        lockedjawPatch: [],
        subfibrosisPatch: [],
        smokerspalatePatch: [],
        lichenPlanusPatch: []
      }
    },
    breast: {
      assesDate: formatTodayApiDate(),
      brstChg: null,
      dmplng: null,
      sknRetrctn: null,
      ulcrtn: null,
      niplRetrctn: null,
      lump: null,
      dschrge: null,
      level: null,
      skinEdema: null,
      isLessSkinEdma: null,
      isMoreSkinEdma: null,
      lymphdnpthy: null,
      axilry: null,
      supra: null,
      infra: null,
      ndeAux: "",
      ndSprsclvclr: "",
      brstExmLctnsPnts: {
        lumpPoints: [],
        ulcerPoints: [],
        nplRtctnPoints: [],
        npldischrgPoints: [],
        dmplngPoints: [],
        retrctnSknPoints: []
      }
    },
    cervical: {
      assesDate: formatTodayApiDate(),
      polyp: null,
      ectopy: null,
      hyptrphy: null,
      dischrg: null,
      proUtrus: null,
      bldTouch: null,
      unCervx: null,
      susCervx: null,
      maligncy: null,
      othr: null,
      othrStr: "",
      crvclLctns: {
        viaTestResult: "",
        viaPoints: null
      }
    }
  });

  const showFemaleForms = (gender || "").trim().toLowerCase() === "female";

  useEffect(() => {
    const fetchPhysicalExam = async () => {
      if (!individualId) return;
      try {
        setIsPageLoading(true);
        const res = await ncdAPI.getInitialAssessmentPhysicalExam(individualId);
        if (res && res.success && res.data) {
          const examData = res.data;
          setFormData((prev) => ({
            oral: examData.oral ? { ...prev.oral, ...examData.oral } : prev.oral,
            breast: examData.breast ? { ...prev.breast, ...examData.breast } : prev.breast,
            cervical: examData.cervical ? { ...prev.cervical, ...examData.cervical } : prev.cervical
          }));
          if (examData.oral) {
            setIsOralEditing(false);
            setHasOralData(true);
          }
          if (examData.breast) {
            setIsBreastEditing(false);
            setHasBreastData(true);
          }
          if (examData.cervical) {
            setIsCervicalEditing(false);
            setHasCervicalData(true);
          }
        }
      } catch (err) {
        console.error("Error fetching physical exam details", err);
      } finally {
        setIsPageLoading(false);
      }
    };
    fetchPhysicalExam();
  }, [individualId]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!individualId) return;

      const isFutureDate = (dateStr) => {
        if (!dateStr) return false;
        const date = dayjs(dateStr, 'DD-MM-YYYY');
        return date.isValid() && date.isAfter(dayjs(), 'day');
      };

      if (isFutureDate(formData.oral?.assesDate)) {
        throw new Error("Oral Assessment Date cannot be a future date.");
      }

      if (showFemaleForms) {
        if (isFutureDate(formData.breast?.assesDate)) {
          throw new Error("Breast Assessment Date cannot be a future date.");
        }
        if (isFutureDate(formData.cervical?.assesDate)) {
          throw new Error("Cervical Assessment Date cannot be a future date.");
        }
      }

      try {
        const payload = {
          oral: formData.oral,
          ...(showFemaleForms ? {
            breast: formData.breast,
            cervical: formData.cervical
          } : {})
        };
        const response = await ncdAPI.submitInitialAssessmentPhysicalExam(individualId, payload);
        return response;
      } catch (err) {
        console.error("Error submitting physical exam details", err);
        throw err;
      }
    }
  }));

  const handleSectionDataChange = (section, sectionData) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...sectionData
      }
    }));
  };

  const sectionStyle = (isEditing) => ({
    position: 'relative',
    filter: isEditing ? 'none' : 'blur(1.3px)',
    opacity: isEditing ? 1 : 0.8,
    pointerEvents: isEditing ? 'auto' : 'none',
    transition: 'all 0.3s ease',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginBottom: '30px',
    backgroundColor: isEditing ? '#fff' : '#fafafa',
  });

  return (
    <Box className="form-section">
      {isPageLoading && <Loader />}
      
      <Typography variant="h5" className="section-title" sx={{ mb: 4 }}>
        Physical Examination
      </Typography>

      {/* Oral Cancer Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" className="section-title" sx={{ m: 0 }}>Oral Cancer</Typography>
          {hasOralData && (
            <Button
              variant="contained"
              size="small"
              color={isOralEditing ? "error" : "primary"}
              onClick={() => setIsOralEditing(!isOralEditing)}
              sx={{ textTransform: 'capitalize' }}
            >
              <EditOutlinedIcon sx={{ fontSize: '18px', mr: 0.5 }} /> {isOralEditing ? 'Cancel' : 'Edit'}
            </Button>
          )}
        </Box>
        <Box sx={sectionStyle(isOralEditing)}>
          <Oralphysical 
            data={formData.oral} 
            onChange={(data) => handleSectionDataChange('oral', data)} 
          />
        </Box>
      </Box>

      {/* Breast Cancer Section */}
      {showFemaleForms && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" className="section-title" sx={{ m: 0 }}>Breast Cancer</Typography>
            {hasBreastData && (
              <Button
                variant="contained"
                size="small"
                color={isBreastEditing ? "error" : "primary"}
                onClick={() => setIsBreastEditing(!isBreastEditing)}
                sx={{ textTransform: 'capitalize' }}
              >
                <EditOutlinedIcon sx={{ fontSize: '18px', mr: 0.5 }} /> {isBreastEditing ? 'Cancel' : 'Edit'}
              </Button>
            )}
          </Box>
          <Box sx={sectionStyle(isBreastEditing)}>
            <Breastphysical 
              data={formData.breast} 
              onChange={(data) => handleSectionDataChange('breast', data)} 
            />
          </Box>
        </Box>
      )}

      {/* Cervical Cancer Section */}
      {showFemaleForms && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" className="section-title" sx={{ m: 0 }}>Cervical Cancer</Typography>
            {hasCervicalData && (
              <Button
                variant="contained"
                size="small"
                color={isCervicalEditing ? "error" : "primary"}
                onClick={() => setIsCervicalEditing(!isCervicalEditing)}
                sx={{ textTransform: 'capitalize' }}
              >
                <EditOutlinedIcon sx={{ fontSize: '18px', mr: 0.5 }} /> {isCervicalEditing ? 'Cancel' : 'Edit'}
              </Button>
            )}
          </Box>
          <Box sx={sectionStyle(isCervicalEditing)}>
            <Cervicalphysical 
              data={formData.cervical} 
              onChange={(data) => handleSectionDataChange('cervical', data)} 
            />
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default PhysicalExaminationForm;
